import { commercialCatalog, type CommercialCatalog } from '../catalog'
import { ComputerBuilder, SimpleItemBuilder } from '../builders'
import { commercialTemplates, type CommercialTemplateRegistry } from '../templates'
import type { CommercialItem, CommitCommercialOptions } from '../types'
import {
  commercialBudgetService,
  CommercialBudgetService,
  type CommercialBudgetTarget,
} from './CommercialBudgetService'

/**
 * Façade for sub-1-minute commercial quoting:
 * catalog / templates → builders → CommercialItem → Budget Engine.
 */
export class CommercialQuoteService {
  private readonly simple: SimpleItemBuilder
  private readonly budget: CommercialBudgetService
  private readonly catalog: CommercialCatalog
  private readonly templates: CommercialTemplateRegistry

  constructor(
    catalog: CommercialCatalog = commercialCatalog,
    templates: CommercialTemplateRegistry = commercialTemplates,
    budgetService: CommercialBudgetService = commercialBudgetService,
  ) {
    this.catalog = catalog
    this.templates = templates
    this.simple = new SimpleItemBuilder(catalog)
    this.budget = budgetService
  }

  listTemplates(tag?: string) {
    return this.templates.list(tag)
  }

  searchCatalog(term: string) {
    return this.catalog.search(term)
  }

  /** Instant Computer from a commercial template. */
  buildComputerFromTemplate(templateId: string): CommercialItem {
    const template = this.templates.get(templateId)
    if (!template || template.itemType !== 'Computer') {
      throw Object.assign(new Error(`Template de computador inválido: ${templateId}`), {
        code: 'TEMPLATE_INVALID',
      })
    }
    if (!template.componentCatalogIds) {
      throw Object.assign(new Error('Template sem componentes'), { code: 'TEMPLATE_EMPTY' })
    }

    const builder = new ComputerBuilder(this.catalog)
      .reset(template.itemName ?? template.name)
      .setDescription(template.description)

    for (const [slot, catalogId] of Object.entries(template.componentCatalogIds)) {
      if (!catalogId) continue
      builder.setSlot(slot as Parameters<ComputerBuilder['setSlot']>[0], catalogId)
    }

    return builder.build({ originTemplateId: template.id })
  }

  /** Instant simple CommercialItem from template. */
  buildSimpleFromTemplate(templateId: string): CommercialItem {
    const template = this.templates.get(templateId)
    if (!template) {
      throw Object.assign(new Error(`Template não encontrado: ${templateId}`), {
        code: 'TEMPLATE_NOT_FOUND',
      })
    }
    if (template.itemType === 'Computer') {
      return this.buildComputerFromTemplate(templateId)
    }
    if (!template.catalogId) {
      throw Object.assign(new Error('Template sem catalogId'), { code: 'TEMPLATE_EMPTY' })
    }
    return this.simple.fromCatalog(template.catalogId, {
      name: template.itemName,
      templateId: template.id,
    })
  }

  /**
   * Apply a fast template and commit into Budget Engine / Editor Adapter.
   * Primary path for “orçamento em menos de 1 minuto”.
   */
  applyTemplate(
    target: CommercialBudgetTarget,
    templateId: string,
    options?: CommitCommercialOptions,
  ) {
    const item = this.buildSimpleFromTemplate(templateId)
    return this.budget.addItem(target, item, options)
  }

  commitItem(
    target: CommercialBudgetTarget,
    item: CommercialItem,
    options?: CommitCommercialOptions,
  ) {
    return this.budget.addItem(target, item, options)
  }

  createComputerBuilder() {
    return new ComputerBuilder(this.catalog)
  }
}

export const commercialQuoteService = new CommercialQuoteService()
